import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { Building2, Boxes, TrendingUp } from 'lucide-react';
import { useMetrics } from './dashboard.api';
import { useTenants } from '@/features/tenants/tenants.api';

const money = (n: number, currency = 'PEN') =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

const STATUS_LABEL: Record<string, string> = {
  active: 'Activas',
  trial: 'Prueba',
  past_due: 'Vencidas',
  suspended: 'Suspendidas',
  cancelled: 'Canceladas',
  none: 'Sin susc.',
};

export function DashboardPage(): JSX.Element {
  const { data: m, isLoading } = useMetrics();
  const { data: tenants } = useTenants();

  if (isLoading || !m) {
    return <p className="text-sm text-muted-foreground">Cargando metricas…</p>;
  }

  const active = m.subscriptionsByStatus.active ?? 0;
  const activationPct = m.tenants ? Math.round((active / m.tenants) * 100) : 0;
  const statusData = Object.entries(m.subscriptionsByStatus).map(([status, count]) => ({
    label: STATUS_LABEL[status] ?? status,
    count,
  }));
  const latest = [...(tenants ?? [])].slice(-5).reverse();

  return (
    <div className="space-y-6">
      {/* Header estilo "Sales Overview" */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Resumen de plataforma</h1>
          <p className="text-sm text-muted-foreground">Estado comercial y operativo de Cajita.</p>
        </div>
        <div className="flex gap-2">
          <button className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground">
            Exportar
          </button>
          <button className="rounded-full bg-gradient-to-r from-primary to-cyan-500 px-4 py-2 text-sm font-medium text-white shadow-sm shadow-primary/30">
            Filtrar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* MRR — card destacada con gradiente azul oscuro */}
        <div className="lg:col-span-4 rounded-2xl bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-5 text-white shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-sm/none text-white/80">MRR estimado</span>
            <TrendingUp className="h-5 w-5 text-white/70" />
          </div>
          <p className="mt-4 text-3xl font-bold tabular-nums">{money(m.mrrEstimated, m.currency)}</p>
          <p className="mt-2 text-xs text-white/70">{m.tenants} empresa(s) · base + add-ons</p>
        </div>

        {/* Empresas */}
        <Kpi
          className="lg:col-span-4"
          icon={<Building2 className="h-5 w-5 text-primary" />}
          label="Empresas"
          value={String(m.tenants)}
          hint="Tenants registrados"
        />

        {/* Modulos activos */}
        <Kpi
          className="lg:col-span-4"
          icon={<Boxes className="h-5 w-5 text-primary" />}
          label="Modulos activos"
          value={String(m.modulesActive)}
          hint="Add-ons aprovisionados"
        />

        {/* Gauge — tasa de activacion */}
        <div className="lg:col-span-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <p className="text-sm font-medium">Tasa de activacion</p>
          <div className="relative mx-auto" style={{ maxWidth: 260 }}>
            <ResponsiveContainer width="100%" height={150}>
              <RadialBarChart
                innerRadius="74%"
                outerRadius="100%"
                data={[{ value: activationPct }]}
                startAngle={180}
                endAngle={0}
                barSize={16}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar
                  dataKey="value"
                  cornerRadius={12}
                  fill="hsl(var(--primary))"
                  background={{ fill: 'hsl(var(--muted))' }}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-x-0 bottom-1 text-center">
              <p className="text-3xl font-bold tabular-nums leading-none">{activationPct}%</p>
              <p className="mt-1 text-xs text-muted-foreground">{active} activas</p>
            </div>
          </div>
        </div>

        {/* Bars — suscripciones por estado */}
        <div className="lg:col-span-8 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <p className="text-sm font-medium mb-2">Suscripciones por estado</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={statusData}
              margin={{ top: 10, right: 8, left: -20, bottom: 0 }}
              barCategoryGap="35%"
            >
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} width={28} />
              <Tooltip
                cursor={{ fill: 'hsl(var(--accent))' }}
                contentStyle={{ borderRadius: 12, border: '1px solid hsl(var(--border))', fontSize: 12 }}
              />
              <Bar dataKey="count" radius={[8, 8, 0, 0]} maxBarSize={56}>
                {statusData.map((_, i) => (
                  <Cell key={i} fill={i % 2 === 0 ? 'hsl(var(--primary))' : '#22d3ee'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Lista — ultimas empresas */}
        <div className="lg:col-span-12 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <p className="text-sm font-medium mb-3">Ultimas empresas</p>
          <ul className="divide-y">
            {latest.length === 0 && (
              <li className="py-3 text-sm text-muted-foreground">Sin empresas aun.</li>
            )}
            {latest.map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-3">
                <div className="h-9 w-9 rounded-full bg-primary/15 text-primary grid place-items-center text-sm font-semibold">
                  {t.businessName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t.businessName}</p>
                  <p className="text-xs text-muted-foreground">RUC {t.ruc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Kpi({
  className,
  icon,
  label,
  value,
  hint,
}: {
  className?: string;
  icon: JSX.Element;
  label: string;
  value: string;
  hint: string;
}): JSX.Element {
  return (
    <div className={`rounded-2xl border border-border bg-card p-5 shadow-sm ${className ?? ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="h-9 w-9 rounded-xl bg-primary/10 grid place-items-center">{icon}</div>
      </div>
      <p className="mt-4 text-3xl font-bold tabular-nums">{value}</p>
      <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
